<template>
    <div :class="root_class" :style="{zIndex: z_index}">
        <div ref="panel" class="cdlg" :class="{'cdlg--docked': docked}" :style="docked ? null : {maxWidth: width}" role="dialog" :aria-modal="docked ? 'false' : 'true'" :aria-labelledby="labelledby" :aria-label="label" tabindex="-1">
            <button v-if="closable" type="button" class="cdlg__close" aria-label="Закрыть" title="Закрыть" @click="$emit('close')">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
            </button>
            <slot/>
        </div>
    </div>
</template>

<script>
// Оболочка окон звонков. Про сами звонки ничего не знает: рисует тёмную рамку с градиентом, крестик закрытия
// и слот для содержимого, а ещё делает то, что у всех окон звонков одинаково. Так «Исходящий звонок»
// (OutgoingNumberPicker) и «Вы услышали человека?» (CallOutcomeDialog) выглядят и ведут себя как одно целое.
//
//  Место (placement):
//   «center»      — по центру страницы на затемнении, страница под ним не нажимается (модальное окно);
//   «bottom-left» — внизу слева, в том же углу и той же ширины, что панель звонка (#block-call), без затемнения:
//                   страница остаётся доступной. raised — поднять окно на высоту панели звонка, если она показана
//                   (идёт или входит звонок), чтобы окно её не закрывало.
//  Закрытие: крестик и Esc шлют событие close, а убирает окно сам родитель (v-if). closable=false — закрыть
//   нельзя (крестика нет, Esc ничего не делает): так работает обязательный вопрос. Клик мимо окна не закрывает.
//  Esc: реагирует только верхнее из открытых окон и не пускает событие дальше, иначе Esc закрыл бы ещё и
//   карточку заявки (el-drawer) под окном.
//  Фокус: при открытии уходит в окно, при закрытии возвращается туда, где был (кнопка «позвонить» в карточке).
//  Слой (z-index): выше панелей телефонии (2990) и открытой карточки заявки, но ниже панели звонка (3000).
//  Оформление для содержимого: .cdlg-title, .cdlg-muted, .cdlg-error, кнопки .cdlg-btn (--yes зелёная, --no красная).

// Открытые окна: верхнее — последнее
const stack = [];

// Слой окна: над панелями телефонии (2990) и под панелью звонка (3000), чтобы входящий можно было принять.
// Счётчик z-index у Element-UI только растёт (каждый выпадающий список и подсказка берут следующее число),
// и в долгой сессии открытая карточка заявки (el-drawer) может оказаться выше — тогда встаём над ней.
const BASE_Z_INDEX = 2995;
function overlayZIndex() {
    let z = BASE_Z_INDEX;
    document.querySelectorAll('.el-drawer__wrapper, .el-dialog__wrapper, .el-message-box__wrapper, .v-modal').forEach(function (el) {
        const style = window.getComputedStyle(el);
        const value = parseInt(style.zIndex, 10);
        if (style.display !== 'none' && value >= z) z = value + 1;
    });
    return z;
}

export default {
    name: "CallDialog",
    props: {
        placement: {type: String, default: 'center', validator: (value) => ['center', 'bottom-left'].indexOf(value) !== -1},
        // Только для bottom-left: стоять над панелью звонка, а не на её месте
        raised: {type: Boolean, default: false},
        closable: {type: Boolean, default: true},
        // Только для center: максимальная ширина окна
        width: {type: String, default: '940px'},
        labelledby: {type: String, default: null},
        label: {type: String, default: null},
    },
    data: () => ({
        z_index: BASE_Z_INDEX,
    }),
    computed: {
        docked() {
            return this.placement === 'bottom-left';
        },
        root_class() {
            return this.docked ? {'cdlg-dock': true, 'cdlg-dock--raised': this.raised} : {'cdlg-overlay': true};
        },
    },
    created() {
        this.z_index = overlayZIndex();
        // Куда вернуть фокус после закрытия (кнопка «позвонить» в карточке заявки)
        this.previous_focus = document.activeElement;
    },
    mounted() {
        stack.push(this);
        window.addEventListener('keydown', this.onKeydown, true);
        this.focus();
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.onKeydown, true);
        const index = stack.indexOf(this);
        if (index !== -1) stack.splice(index, 1);
        const previous = this.previous_focus;
        if (previous && typeof previous.focus === 'function' && document.body.contains(previous)) {
            try {previous.focus({preventScroll: true});} catch (e) { /* элемент уже недоступен для фокуса */ }
        }
    },
    methods: {
        /** Фокус на окно: Esc и Tab начинаются внутри окна, а не на странице под ним */
        focus() {
            const panel = this.$refs.panel;
            if (panel && !panel.contains(document.activeElement)) panel.focus({preventScroll: true});
        },
        onKeydown(e) {
            if (e.key !== 'Escape' || stack[stack.length - 1] !== this) return;
            // Иначе Esc закроет ещё и карточку заявки (el-drawer) под окном
            e.stopPropagation();
            e.preventDefault();
            if (this.closable) this.$emit('close');
        },
    },
}
</script>

<style lang="scss">
// Слои: карточка заявки (el-drawer) ~2000 → панель настроек / очередь / ACW 2990 → окна звонков 2995 → панель звонка 3000.
// Панель звонка выше окна: входящий можно принять, не закрывая выбор номера. Точный z-index — overlayZIndex().

// placement="center": затемнение на всю страницу и окно по центру
.cdlg-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2995;
    display: flex;
    padding: 16px;
    overflow-y: auto;
    background: #10192399;
    animation: cdlg-fade .15s ease-out;
}
// placement="bottom-left": в углу панели звонка, той же ширины. Размеры панели звонка задаёт WebPhone.vue
// (--webphone-panel-width / --webphone-panel-height), чтобы окно и панель не разъезжались.
.cdlg-dock {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 2995;
    display: flex;
    width: var(--webphone-panel-width, 700px);
    max-width: 100%;
    transition: bottom .2s ease;
}
.cdlg-dock--raised {
    bottom: var(--webphone-panel-height, 110px);
}
.cdlg {
    position: relative;
    box-sizing: border-box;
    width: 100%;
    margin: auto;
    color: #f1f5f9;
    font-size: 15px;
    line-height: 1.4;
    background: linear-gradient(110deg, #2e3b4c, #253649);
    border: 1px solid #435266;
    border-radius: 5px;
    box-shadow: 0 12px 35px #17283c20;
    outline: none;
    animation: cdlg-rise .15s ease-out;

    button { font-family: inherit; }
}
// В углу окно прижато к краям экрана, как панель звонка: рамка и скругление только у свободных сторон
.cdlg--docked {
    margin: 0;
    max-height: 100vh;
    overflow-y: auto;
    border-width: 1px 1px 0 0;
    border-radius: 0 5px 0 0;
    box-shadow: 0 0 28px rgba(16, 25, 35, .35);
    animation: cdlg-dock-in .18s ease-out;
}
.cdlg-dock--raised .cdlg--docked {
    max-height: calc(100vh - var(--webphone-panel-height, 110px));
}
.cdlg-title {
    margin: 0 40px 5px 0;
    font-size: 21px;
    font-weight: 400;
    line-height: 1.3;
    color: inherit;
}
.cdlg-muted { color: #aabbd0; }
.cdlg-error {
    margin: 0;
    color: #ffc9c9;
}
.cdlg__close {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    padding: 0;
    color: #c8d5e4;
    background: transparent;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    transition: background .15s, color .15s;

    svg {
        width: 18px;
        height: 18px;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
    }
    &:hover { background: #ffffff12; color: #fff; }
    &:active { background: #ffffff20; }
}
// Кнопки ответа: зелёная — «Позвонить» / «Да», красная — «Нет»
.cdlg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #fff;
    font-size: 16px;
    line-height: 1.3;
    border: 0;
    border-radius: 5px;
    cursor: pointer;
    transition: filter .15s;

    &:hover:enabled { filter: brightness(1.12); }
    &:active:enabled { filter: brightness(.92); }
    &:disabled { opacity: .45; cursor: not-allowed; }
    svg {
        width: 19px;
        height: 19px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.8;
    }
}
.cdlg-btn--yes { background: #329b3b; }
.cdlg-btn--no { background: #e31c36; }
.cdlg-btn:focus,
.cdlg__close:focus { outline: none; }
.cdlg-btn:focus-visible,
.cdlg__close:focus-visible {
    outline: 2px solid #79c5ff;
    outline-offset: 3px;
}
@keyframes cdlg-fade {
    from { opacity: 0; }
    to { opacity: 1; }
}
@keyframes cdlg-rise {
    from { transform: translateY(-8px); }
    to { transform: none; }
}
@keyframes cdlg-dock-in {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
    .cdlg-overlay, .cdlg { animation: none; }
    .cdlg-dock { transition: none; }
    .cdlg-btn, .cdlg__close { transition: none; }
}
</style>
